import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Autocomplete,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import { IFactura } from "../Facturas";
import IPaciente from "../../../types/Paciente";
import IServicio from "../../../types/Servicio";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfigUtils from "../../../utils/Config";
import * as constantes from "../../../common/Constantes";
import Utilities from "../../../common/Utilities";
import { IConcepto, IImpuesto } from "./DetalleFactura";
import { buildFacturaComplementariaPayload, IFacturaComplementariaErrors, IFacturaRectificativaPayload, validateFacturaComplementaria, formatCurrency, monedasPresentes, formatNumeroFactura } from "../../../utils/Factura";
import { IFacturaMetodoPago } from "./FacturarCitaDialog";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import LogService from "../../../services/LogService";
import ILog from "../../../types/Log";
import FacturacionService from "../../../services/FacturacionService";
import IAlert from "../../../types/Alert";
import { IFacturaCompleta } from "./DetalleFacturaComplementaria";
import { EstadoFacturacion } from "../common/EstadoFacturacionIcon";

interface Props {
  open: boolean;
  onClose: () => void;
  facturasParciales: IFactura[];
  pacientes: IPaciente[];
  onFacturaCreada?: ({ facturaComplementaria, paciente, tipoFacturacion }: { facturaComplementaria: IFacturaCompleta, paciente?: IPaciente, tipoFacturacion?: string }) => void;
  onFacturaActualizada?: ({ facturaComplementaria, parcialesAsociadas, parcialesDesasociadas }: { facturaComplementaria: IFacturaCompleta, parcialesAsociadas: string[], parcialesDesasociadas: string[] }) => void;
  isEditMode?: boolean;
  isRectificativa?: boolean;
  facturaComplementariaEdit?: IFacturaCompleta;
  onFacturaRectificada?: ({ factura, tipoFacturacion, paciente }: { factura: IFacturaCompleta, tipoFacturacion: string, paciente: IPaciente }) => void;
}

const ComplementariaFormDialog = ({
  open,
  onClose,
  facturasParciales,
  pacientes,
  onFacturaCreada,
  onFacturaActualizada,
  isEditMode = false,
  isRectificativa = false,
  facturaComplementariaEdit,
  onFacturaRectificada
}: Props) => {
  const monedaFactura = (isEditMode || isRectificativa)
    ? (facturaComplementariaEdit?.moneda || 'EUR')
    : (ConfigUtils.getUserCurrency() || 'EUR');
  const history = useNavigate();
  const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
  const [loadingInit, setLoadingInit] = useState<boolean>(false);
  const [paciente, setPaciente] = useState<IPaciente | null>(null);
  const [servicio, setServicio] = useState<IServicio | null>(null);
  const [conceptosExpanded, setConceptosExpanded] = useState<boolean>(false);
  const [conceptos, setConceptos] = useState<IConcepto[]>([]);
  const [impuestos, setImpuestos] = useState<IImpuesto[]>([]);
  const [inputPrecios, setInputPrecios] = useState<{ [index: number]: string }>({});
  const [errors, setErrors] = useState<IFacturaComplementariaErrors>({});
  const [metodoPago, setMetodoPago] = useState<IFacturaMetodoPago | undefined>(undefined);
  const [facturaPagada, setFacturaPagada] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [servicioEdicion, setServicioEdicion] = useState<string>("");
  const [facturasSinComplementaria, setFacturasSinComplementaria] = useState<string[]>([]);
  const [selectedFactura, setSelectedFactura] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [nuevaFacturaRectificativa, setNuevaFacturaRectificativa] = useState<IFacturaRectificativaPayload | null>(null);

  const userId = ConfigUtils.getUserId();
  const servicios = ConfigUtils.getServicios();
  const metodosPago = ConfigUtils.getMetodosPago();

  const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

  const clearGeneralError = () => {
    setErrors(prev => {
      if (!prev.general) return prev;
      return { ...prev, general: undefined };
    });
  };

  const handleCrearFactura = async () => {
    const validationErrors = validateFacturaComplementaria({ paciente, servicio, conceptos, facturaPagada, metodoPago });

    if (monedasPresentes(facturasParciales).length > 1) {
      validationErrors.general = "Las parciales seleccionadas tienen divisas distintas: no se pueden agrupar";
    }

    setErrors(validationErrors);
    if (validationErrors.conceptos) {
      setConceptosExpanded(true);
    }
    if (Object.keys(validationErrors).length > 0) return;

    const facturasIds = facturasParciales.map(f => f._id);
    const nuevaFactura = buildFacturaComplementariaPayload({
      userId: ConfigUtils.getUserId(),
      pacienteId: paciente?._id!,
      servicio: servicio?.nombre!,
      conceptos,
      impuestos,
      metodoPago,
      facturasIds
    });

    try {
      setLoading(true);
      
      const response = await FacturacionService.createFacturaComplementaria(nuevaFactura);
      if (onFacturaCreada) onFacturaCreada(response.data);
      onClose();
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "ComplementariaFormDialog.Component.handleCrearFactura",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditarFactura = async () => {
    const validationErrors = validateFacturaComplementaria({ paciente, servicio: servicioEdicion, conceptos, facturaPagada, metodoPago, isEdit:true });
    setErrors(validationErrors);
    if (validationErrors.conceptos) {
      setConceptosExpanded(true);
    }
    if (Object.keys(validationErrors).length > 0) return;

    const facturasIds = conceptos.map(c => c.facturaParcialId!);
    const updatedFactura = buildFacturaComplementariaPayload({
      userId: ConfigUtils.getUserId(),
      pacienteId: paciente?._id!,
      servicio: servicioEdicion,
      conceptos,
      impuestos,
      metodoPago,
      facturasIds
    });

    try {
      setLoading(true);
      
      const response = await FacturacionService.editFacturaComplementaria(facturaComplementariaEdit?._id!, updatedFactura);
      if (onFacturaActualizada) onFacturaActualizada(response.data);
      onClose();
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "ComplementariaFormDialog.Component.handleEditarFactura",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const validateRectificativa = () => {
    setErrors(prev => ({ ...prev, general: undefined }));
    
    const validationErrors = validateFacturaComplementaria({
      paciente,
      servicio: servicioEdicion,
      conceptos,
      facturaPagada,
      metodoPago,
      isRectificativa: true
    }); 

    setErrors(validationErrors);  

    if (validationErrors.conceptos) {
      setConceptosExpanded(true);
    } 

    if (Object.keys(validationErrors).length > 0) return; 

    const original = facturaComplementariaEdit!;
    const pacienteOriginal = Utilities.resolverPacienteFactura(pacientes, original);
    const mismaFechaPago =
    original.metodoPago?.fechaPago &&
    metodoPago?.fechaPago &&
      moment(original.metodoPago.fechaPago).isSame(
      moment(metodoPago.fechaPago),
      'day'
    );
    const cambiosFiscales =
      pacienteOriginal?._id !== paciente?._id ||
      original.servicio !== servicioEdicion ||
      // Quitar o añadir una parcial cambia el número de conceptos: sin esto, quitar una pasaba por "solo cambio de cobro".
      conceptos.length !== (original.conceptos?.length ?? 0) ||
      conceptos.some((c, i) => {
        const orig = original.conceptos?.[i];
        const impuesto = impuestos?.[i];
        return (
          !orig ||
          orig.facturaParcialId !== c.facturaParcialId ||
          orig.nombre !== c.nombre ||
          orig.cantidad !== c.cantidad ||
          orig.precio !== c.precio ||
          impuesto?.tipo !== original?.impuestos?.[i]?.tipo ||
          impuesto?.base !== original?.impuestos?.[i]?.base ||
          impuesto?.cuota !== original?.impuestos?.[i]?.cuota
        );
      });

    const cambioPago =
      original.metodoPago?.nombre !== metodoPago?.nombre ||
      original.metodoPago?.codigo !== metodoPago?.codigo ||
      !mismaFechaPago;

    if (!cambiosFiscales && cambioPago) {
      setErrors(prev => ({ ...prev, general: "Solo has cambiado el método o la fecha de pago — usa \"Corregir cobro\" en vez de crear una rectificativa" }));
      return;
    }

    if (!cambiosFiscales && !cambioPago) {
      setErrors(prev => ({ ...prev, general: "No se han realizado cambios respecto a la factura original" }));
      return;
    }

    const payload: IFacturaRectificativaPayload = {
      userId: ConfigUtils.getUserId(),
      pacienteId: paciente?._id!,
      servicio: servicioEdicion,
      conceptos,
      impuestos,
      tipoPago: "parcial",
      tipoFactura: "agrupada",
      metodoPago,
      facturaOriginalId: facturaComplementariaEdit?.numero!,
      bonoId: "",
      devolverSesiones: false
    };  

    setNuevaFacturaRectificativa(payload);
    setConfirmOpen(true);
  };

  const handleConfirmRectificativa = async () => {
    if (!nuevaFacturaRectificativa) return;
    setConfirmOpen(false);
    setLoading(true);

    try {
      const response = await FacturacionService.createFacturaRectificativa(nuevaFacturaRectificativa);
      if (onFacturaRectificada) onFacturaRectificada(response.data);
      onClose();
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "ComplementariaFormDialog.Component.handleConfirmRectificativa",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditRectificativa = async () => {
    const validationErrors = validateFacturaComplementaria({
      paciente,
      servicio: servicioEdicion,
      conceptos,
      facturaPagada,
      metodoPago,
      isRectificativa: true
    }); 

    setErrors(validationErrors);  

    if (validationErrors.conceptos) {
      setConceptosExpanded(true);
    } 

    if (Object.keys(validationErrors).length > 0) return; 

    const updatedFactura: IFacturaRectificativaPayload = {
      userId: ConfigUtils.getUserId(),
      pacienteId: paciente?._id!,
      servicio: servicioEdicion,
      conceptos,
      impuestos,
      tipoPago: "parcial",
      tipoFactura: "agrupada",
      metodoPago,
      facturaOriginalId: facturaComplementariaEdit?.numero!,
      bonoId: "",
      devolverSesiones: false
    }

    try {
      setLoading(true);
      
      const response = await FacturacionService.editFacturaRectificativa(facturaComplementariaEdit?._id!, updatedFactura);
      if (onFacturaActualizada) onFacturaActualizada(response.data);
      onClose();
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "ComplementariaFormDialog.Component.handleEditRectificativa",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }

  };

  const importeBase = conceptos.reduce((acc, c) => acc + c.precio, 0);

  const importeImpuestos = conceptos.reduce((acc, c, index) => {
    const imp = impuestos[index];
    return acc + c.precio * (imp?.cuota ?? 0) / 100;
  }, 0);

  const importeTotal = importeBase + importeImpuestos;

  useEffect(() => {
    if (!isRectificativa && (!isEditMode || !facturaComplementariaEdit)) return;
    if (!facturaComplementariaEdit) return;
    
    const getFacturasParciales = async () => {
      try {
        setLoadingInit(true);
        const response = await FacturacionService.getFacturasParciales(userId);
        setFacturasSinComplementaria(
          response.data
            .filter(f => f.facturaRelacionada === null && f.tipo !== 'agrupada' && (f.entityId ?? null) === (facturaComplementariaEdit.entityId ?? null) && (f.estado === EstadoFacturacion.EMITIDA || f.estado === EstadoFacturacion.EMITIDA_PENDIENTE_PAGO))
            .map(f => f.numero)
            .filter((n): n is string => !!n)
        );
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.clear();
          history(constantes.R_HOME);
        } else {
          const error: ILog = {
            method: "ComplementariaFormDialog.Component.getFacturasParciales",
            level: "error",
            message: e.message,
            meta: e.response,
          };
          LogService.logError(error);
          setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
        }
      } finally {
        setLoadingInit(false);
      }
    };
    getFacturasParciales();
  
    setPaciente(Utilities.resolverPacienteFactura(pacientes, facturaComplementariaEdit));

    setConceptos(
      facturaComplementariaEdit.conceptos.map(c => ({
        nombre: c.nombre,
        cantidad: c.cantidad,
        precio: c.precio,
        facturaParcialId: c.facturaParcialId,
      }))
    );
  
    setImpuestos(
      facturaComplementariaEdit.impuestos.map(c => ({
        tipo: "IVA",
        base: c.base,
        cuota: c.cuota ?? 0,
      }))
    );
  
    if (facturaComplementariaEdit.metodoPago) {
      setFacturaPagada(true);
      setMetodoPago({
        nombre: facturaComplementariaEdit.metodoPago.nombre,
        codigo: facturaComplementariaEdit.metodoPago.codigo,
        fechaPago: moment(facturaComplementariaEdit.metodoPago.fechaPago),
      });
    } else {
      setFacturaPagada(false);
      setMetodoPago(undefined);
    }
  
    setServicioEdicion(facturaComplementariaEdit.servicio);
    setConceptosExpanded(true);
  
  }, [
    isEditMode,
    facturaComplementariaEdit?._id,
    pacientes,
  ]);

  const removeFacturaParcial = (facturaParcialId: string) => {
    const index = conceptos.findIndex(c => c.facturaParcialId === facturaParcialId);
    if (index === -1) return;

    setConceptos(prev => prev.filter((_, i) => i !== index));
    setImpuestos(prev => prev.filter((_, i) => i !== index));

    setFacturasSinComplementaria(prev => [...prev, facturaParcialId]);
  };
  
  return (
    <>
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>

      {(isEditMode || isRectificativa) && loadingInit ? <CircularProgress style={{ margin: "20px auto" }} /> : 
      <>
        {(!isEditMode && !isRectificativa) && <DialogTitle>
        Crear factura complementaria
        </DialogTitle>}
        {isRectificativa && !isEditMode && <DialogTitle>
        {`Rectificar factura nº: ${facturaComplementariaEdit?.numero}`}
        </DialogTitle>}

        { isEditMode && 
          <Box
            sx={{
              p: 2,
              backgroundColor: "#f8f9fa",
              border: "1px solid #e5e5e5",
            }}
          >     
            <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between">
              {/* Datos del receptor */}
              <Box flex={1}>
                <Typography variant="h6" gutterBottom>
                  Receptor
                </Typography>
                <Typography variant="body2">
                  <b>Nombre:</b> {facturaComplementariaEdit?.receptor.nombre}
                </Typography>
                {facturaComplementariaEdit?.receptor.nif && 
                <Typography variant="body2">
                  <b>NIF:</b> {facturaComplementariaEdit?.receptor.nif}
                </Typography>}
                {facturaComplementariaEdit?.receptor.direccion && 
                <Typography variant="body2">
                  <b>Domicilio:</b> {facturaComplementariaEdit?.receptor.direccion}
                </Typography>}
                {facturaComplementariaEdit?.receptor.email && 
                <Typography variant="body2">
                  <b>Email:</b> {facturaComplementariaEdit?.receptor.email}
                </Typography>}
              </Box>
            </Box>
          </Box>
        }

        <DialogContent dividers={!isEditMode}>

          {isEditMode &&
            <Box mb={2}>
              <Box display="flex" flexWrap="wrap" gap={4} alignItems="center">
                {/* Número */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Nº:</Typography>
                  {<Typography variant="body2">{formatNumeroFactura(facturaComplementariaEdit?.numero)}</Typography>}
                </Box>
                {/* Fecha */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Fecha factura:</Typography>
                  <Typography variant="body2">
                    {new Date(facturaComplementariaEdit?.fechaFactura!).toLocaleDateString()}
                  </Typography>
                </Box>
                {/* Tipo de factura */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Tipo de factura:</Typography>
                  {<Typography variant="body2">Complementaria</Typography>}
                </Box>
              </Box>
            </Box>
          }

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {/* RECEPTOR */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                size={isEditMode ? "small" : "medium"}
                disablePortal
                autoComplete
                id="pacientes"
                options={pacientes}
                value={paciente}
                onChange={(_, v) => {setPaciente(v!); clearGeneralError(); setErrors((prev: IFacturaComplementariaErrors) => ({ ...prev, paciente: undefined })) }}
                getOptionLabel={(option) => `${option.nombre} ${option.apellidos}` || ""}
                noOptionsText={'No se han encontrado pacientes'}
                renderOption={(props, option) => { return (<li {...props} key={option._id}>{`${option.nombre} ${option.apellidos}`}</li>) }}
                renderInput={(params) => (
                  <TextField {...params} label="Receptor de la factura*" error={!!errors.paciente} helperText={errors.paciente} />)}
                filterOptions={(options, params) => Utilities.filterPacientesAutocomplete(options, params.inputValue)}
              />
            </Grid>

          {/* SERVICIO */}
          {!isEditMode && !isRectificativa ? 
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={servicios.filter(s => s.activo && !s.recurrente)}
              getOptionLabel={(o) => o.nombre}
              value={servicio}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(_, v) => {
                  if (v) {
                    setServicio(v);
                    setConceptosExpanded(true);
                    setErrors((prev: IFacturaComplementariaErrors) => ({ ...prev, servicio: undefined }))
                  }
                  if (!v) {
                    setServicio(null);
                    setConceptos([]);
                    setImpuestos([]);
                    setConceptosExpanded(false);
                    return;
                  }
                  const nuevosConceptos: IConcepto[] = facturasParciales.map((f) => ({
                    nombre: v.nombre,
                    cantidad: 1,
                    precio: v.precio,
                    facturaParcialId: f.numero
                  }));
                  const nuevosImpuestos: IImpuesto[] = facturasParciales.map((f) => ({
                    tipo: "IVA",
                    base: f.total,
                    cuota: v.impuesto ?? 0,
                  }));
                  setConceptos(nuevosConceptos);
                  setImpuestos(nuevosImpuestos);
                }
              }
              renderInput={(params) => (
                <TextField {...params} error={!!errors.servicio} helperText={errors.servicio} label="Servicio*" />
              )}
          /></Grid> : 
          <Grid item xs={12} md={6}>
            <TextField
              size={isEditMode ? "small" : "medium"}
              label="Servicio*"
              value={servicioEdicion}
              fullWidth
              onChange={(e) => {
                setServicioEdicion(e.target.value);
                clearGeneralError();
                setErrors((prev: IFacturaComplementariaErrors) => ({ ...prev, servicio: undefined }));
              }}
              error={!!errors.servicio}
              helperText={errors.servicio}
            />
          </Grid>}
          </Grid>

          {isEditMode && !isRectificativa && 
            <Box mb={2} display="flex" gap={2} alignItems="center" width="100%">
              <Autocomplete
                size="small"
                options={facturasSinComplementaria}
                getOptionLabel={(f) => f}
                value={selectedFactura || null}
                onChange={(_, selected) => setSelectedFactura(selected || null)}
                sx={{ flexGrow: 1 }}
                renderInput={(params) => <TextField {...params} label="Factura parcial" fullWidth />}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!selectedFactura}
                onClick={() => {
                  if (!selectedFactura) return;
                  setConceptos(prev => [
                    ...prev,
                    { nombre: "", cantidad: 1, precio: 0, facturaParcialId: selectedFactura }
                  ]);
                  setImpuestos(prev => [...prev, { tipo: "IVA", base: 0, cuota: 0 }]);

                  setSelectedFactura(null);
                  setErrors((prev: IFacturaComplementariaErrors) => ({ ...prev, minimoConceptos: undefined }));
                  setFacturasSinComplementaria(prev => prev.filter(f => f !== selectedFactura));
                }}
              >
                Añadir concepto
              </Button>
            </Box>
          }

          {!isEditMode && facturasParciales.length > 0 && (
            <Box mb={1}>
              <Accordion
                expanded={conceptosExpanded}
                onChange={() => setConceptosExpanded(prev => !prev)}
              >
              <AccordionSummary expandIcon={conceptosExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Typography variant="subtitle2">
                    {`${facturasParciales.length} facturas parciales asociadas`}
                  </Typography>
                  {/* Totales visibles */}
                  <Box display="flex" gap={3} mr={2}>
                    <Typography variant="body2">
                      Base: <strong>{formatCurrency(importeBase, monedaFactura)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Total: <strong>{formatCurrency(importeTotal, monedaFactura)}</strong>
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {conceptos.length > 0 ? 
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Conceptos de la factura complementaria:
                  </Typography>
                  <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nº Factura</TableCell>
                      <TableCell>Concepto</TableCell>
                      <TableCell align="right" sx={{ width: "13%" }}>Precio</TableCell>
                      <TableCell align="right" sx={{ width: "13%" }}>Impuesto %</TableCell>
                      <TableCell align="right" sx={{ width: "13%" }}>Base</TableCell>
                      <TableCell align="right" sx={{ width: "13%" }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                
                  <TableBody>
                    {facturasParciales.map((factura, index) => {
                      const concepto = conceptos[index];
                      const impuesto = impuestos[index];
                    
                      const base = concepto.precio;
                      const iva = base * (impuesto?.cuota ?? 0) / 100;
                      const total = base + iva;
                    
                      return (
                        <TableRow key={factura._id}>
                          <TableCell>
                            <Box display="flex"
                             alignItems="center"
                             justifyContent="space-between"
                             gap={0.5}
                            >
                              <Typography>
                                {factura?.numero || "—"}
                              </Typography>

                              {errors.conceptos?.[index]?.facturaParcialId && (
                                <Tooltip title={errors.conceptos[index].facturaParcialId}>
                                  <ErrorOutlineIcon
                                    color="error"
                                    fontSize="small"
                                    sx={{ flexShrink: 0 }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                            
                          <TableCell>
                            <TextField
                              variant="outlined"
                              value={concepto.nombre}
                              size="small"
                              onChange={(e) => {
                                const value = e.target.value;
                                setConceptos(prev =>
                                  prev.map((c, i) =>
                                    i === index ? { ...c, nombre: value } : c
                                  )
                                );
                                 setErrors(prev => {
                                  if (!prev.conceptos?.[index]?.concepto) return prev;

                                  const nuevos = [...(prev.conceptos ?? [])];
                                  nuevos[index] = { ...nuevos[index], concepto: undefined };

                                  return { ...prev, conceptos: nuevos };
                                });
                              }}
                              fullWidth
                              error={!!errors.conceptos?.[index]?.concepto}
                              InputProps={{
                                endAdornment: errors.conceptos?.[index]?.concepto ? (
                                  <Tooltip title={errors.conceptos[index].concepto}>
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                  </Tooltip>
                                ) : undefined,
                              }}
                            />
                          </TableCell>
                            
                          <TableCell align="right">
                            <TextField
                              variant="outlined"
                              type="text"
                              value={inputPrecios[index] ?? concepto.precio.toFixed(2).replace(".", ",")}
                              size="small"
                              error={!!errors.conceptos?.[index]?.precio}
                              InputProps={{
                                endAdornment: errors.conceptos?.[index]?.precio ? (
                                  <Tooltip title={errors.conceptos[index].precio}>
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                  </Tooltip>
                                ) : undefined,
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (/^\d*([,]\d*)?$/.test(raw)) {
                                  const normalized = raw.replace(",", ".");
                                  const nuevoPrecio = parseFloat(normalized);
                                
                                  setConceptos(prev =>
                                    prev.map((c, i) =>
                                      i === index
                                        ? { ...c, precio: isNaN(nuevoPrecio) ? 0 : nuevoPrecio }
                                        : c
                                    )
                                  );

                                  setErrors(prev => {
                                    if (!prev.conceptos?.[index]?.precio) return prev;

                                    const nuevos = [...(prev.conceptos ?? [])];
                                    nuevos[index] = { ...nuevos[index], precio: undefined };

                                    return { ...prev, conceptos: nuevos };
                                  });
                                
                                  setInputPrecios(prev => ({ ...prev, [index]: raw }));
                                }
                              }}
                              onBlur={(e) => {
                                setInputPrecios(prev => {
                                  const copy = { ...prev };
                                  delete copy[index];
                                  return copy;
                                });
                              }}
                            />
                          </TableCell>
                            
                          <TableCell align="right">
                            <FormControl size="small" sx={{ width: "80px" }}>
                              <Select
                                value={impuesto?.cuota ?? 0}
                                onChange={(e) => {
                                  const value = Number(e.target.value);
                                  setImpuestos(prev =>
                                    prev.map((imp, i) =>
                                      i === index ? { ...imp, cuota: value } : imp
                                    )
                                  );
                                }}
                              >
                                {constantes.IMPUESTOS.map((imp) => (
                                  <MenuItem key={imp} value={imp}>
                                    {imp}%
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                              
                          <TableCell align="right">
                            {formatCurrency(base, monedaFactura)}
                          </TableCell>
                              
                          <TableCell align="right">
                            {formatCurrency(total, monedaFactura)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  
                  </Table>
                </>
                : 
                <Typography variant="body2" color="text.secondary">
                  Selecciona un servicio para generar los conceptos
                </Typography>
                }

              </AccordionDetails>
            </Accordion>
          </Box>
          )}

          {isEditMode && !isRectificativa && (
            <Box mb={1}>
              <Accordion
                expanded={conceptosExpanded}
                onChange={() => setConceptosExpanded(prev => !prev)}
              >
              <AccordionSummary expandIcon={conceptosExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  {errors.minimoConceptos && (
                    <Tooltip title={errors.minimoConceptos}>
                      <ErrorOutlineIcon
                        color="error"
                        fontSize="small"
                        sx={{ flexShrink: 0 }}
                      />
                    </Tooltip>
                  )}
                  <Typography variant="subtitle2">
                    {`${conceptos.length} facturas parciales asociadas`}
                  </Typography>
                  {/* Totales visibles */}
                  <Box display="flex" gap={3} mr={2}>
                    <Typography variant="body2">
                      Base: <strong>{formatCurrency(importeBase, monedaFactura)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Total: <strong>{formatCurrency(importeTotal, monedaFactura)}</strong>
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {conceptos.length > 0 && 
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Conceptos de la factura complementaria:
                  </Typography>

                  <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>Nº Factura</TableCell>
                      <TableCell>Concepto</TableCell>
                      <TableCell align="right" sx={{ width: "13%" }}>Precio</TableCell>
                      <TableCell align="right" sx={{ width: "13%" }}>Impuesto %</TableCell>
                      <TableCell align="right" sx={{ width: "13%" }}>Base</TableCell>
                      <TableCell align="right" sx={{ width: "13%" }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                
                  <TableBody>
                    {conceptos.map((concepto, index) => {
                      const impuesto = impuestos[index];
                    
                      const base = concepto.precio;
                      const iva = base * (impuesto?.cuota ?? 0) / 100;
                      const total = base + iva;
                    
                      return (
                        <TableRow key={concepto.facturaParcialId}>
                          <TableCell align="center">
                            <Tooltip title="Eliminar factura parcial asociada">
                              <IconButton
                                size="small"
                                onClick={() => removeFacturaParcial(concepto.facturaParcialId!)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Box display="flex"
                             alignItems="center"
                             justifyContent="space-between"
                             gap={0.5}
                            >
                              <Typography>
                                {concepto.facturaParcialId || "—"}
                              </Typography>

                              {errors.conceptos?.[index]?.facturaParcialId && (
                                <Tooltip title={errors.conceptos[index].facturaParcialId}>
                                  <ErrorOutlineIcon
                                    color="error"
                                    fontSize="small"
                                    sx={{ flexShrink: 0 }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                            
                          <TableCell>
                            <TextField
                              variant="outlined"
                              value={concepto.nombre}
                              size="small"
                              onChange={(e) => {
                                const value = e.target.value;
                                setConceptos(prev =>
                                  prev.map((c, i) =>
                                    i === index ? { ...c, nombre: value } : c
                                  )
                                );
                                 setErrors(prev => {
                                  if (!prev.conceptos?.[index]?.concepto) return prev;

                                  const nuevos = [...(prev.conceptos ?? [])];
                                  nuevos[index] = { ...nuevos[index], concepto: undefined };

                                  return { ...prev, conceptos: nuevos };
                                });
                              }}
                              fullWidth
                              error={!!errors.conceptos?.[index]?.concepto}
                              InputProps={{
                                endAdornment: errors.conceptos?.[index]?.concepto ? (
                                  <Tooltip title={errors.conceptos[index].concepto}>
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                  </Tooltip>
                                ) : undefined,
                              }}
                            />
                          </TableCell>
                            
                          <TableCell align="right">
                            <TextField
                              variant="outlined"
                              type="text"
                              value={inputPrecios[index] ?? concepto.precio.toFixed(2).replace(".", ",")}
                              size="small"
                              error={!!errors.conceptos?.[index]?.precio}
                              InputProps={{
                                endAdornment: errors.conceptos?.[index]?.precio ? (
                                  <Tooltip title={errors.conceptos[index].precio}>
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                  </Tooltip>
                                ) : undefined,
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (/^\d*([,]\d*)?$/.test(raw)) {
                                  const normalized = raw.replace(",", ".");
                                  const nuevoPrecio = parseFloat(normalized);
                                
                                  setConceptos(prev =>
                                    prev.map((c, i) =>
                                      i === index
                                        ? { ...c, precio: isNaN(nuevoPrecio) ? 0 : nuevoPrecio }
                                        : c
                                    )
                                  );

                                  setErrors(prev => {
                                    if (!prev.conceptos?.[index]?.precio) return prev;

                                    const nuevos = [...(prev.conceptos ?? [])];
                                    nuevos[index] = { ...nuevos[index], precio: undefined };

                                    return { ...prev, conceptos: nuevos };
                                  });
                                
                                  setInputPrecios(prev => ({ ...prev, [index]: raw }));
                                }
                              }}
                              onBlur={(e) => {
                                setInputPrecios(prev => {
                                  const copy = { ...prev };
                                  delete copy[index];
                                  return copy;
                                });
                              }}
                            />
                          </TableCell>
                            
                          <TableCell align="right">
                            <FormControl size="small" sx={{ width: "80px" }}>
                              <Select
                                value={impuesto?.cuota ?? 0}
                                onChange={(e) => {
                                  const value = Number(e.target.value);
                                  setImpuestos(prev =>
                                    prev.map((imp, i) =>
                                      i === index ? { ...imp, cuota: value } : imp
                                    )
                                  );
                                }}
                              >
                                {constantes.IMPUESTOS.map((imp) => (
                                  <MenuItem key={imp} value={imp}>
                                    {imp}%
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                              
                          <TableCell align="right">
                            {formatCurrency(base, monedaFactura)}
                          </TableCell>
                              
                          <TableCell align="right">
                            {formatCurrency(total, monedaFactura)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  
                  </Table>
                </>}

              </AccordionDetails>
            </Accordion>
          </Box>
          )}

          {isRectificativa && (
            <Box mb={1}>
              <Accordion
                expanded={conceptosExpanded}
                onChange={() => setConceptosExpanded(prev => !prev)}
              >
              <AccordionSummary expandIcon={conceptosExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Button 
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      clearGeneralError();
                      setConceptos(prev => [
                         ...prev,
                         { nombre: "", cantidad: 1, precio: 0 }
                      ]);
                      setImpuestos(prev => [
                        ...prev,
                        { tipo: "IVA", base: 0, cuota: 0 }
                      ]);
                      setErrors((prev: IFacturaComplementariaErrors) => ({
                        ...prev,
                        minimoConceptos: undefined
                      }));
                    }}
                  >
                    Añadir concepto
                  </Button>
                  {errors.minimoConceptos && (
                    <Tooltip title={errors.minimoConceptos}>
                      <ErrorOutlineIcon
                        color="error"
                        fontSize="small"
                        sx={{ flexShrink: 0 }}
                      />
                    </Tooltip>
                  )}
                  {/* Totales visibles */}
                  <Box display="flex" gap={3} mr={2}>
                    <Typography variant="body2">
                      Base: <strong>{formatCurrency(importeBase, monedaFactura)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Total: <strong>{formatCurrency(importeTotal, monedaFactura)}</strong>
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {conceptos.length > 0 && 
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Conceptos de la factura:
                  </Typography>

                  <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell sx={{ minWidth: 260 }}>Concepto</TableCell>
                      <TableCell align="right" sx={{ minWidth: 140 }}>Precio</TableCell>
                      <TableCell align="right" sx={{ minWidth: 120 }}>Impuesto %</TableCell>
                      <TableCell align="right" sx={{ minWidth: 120 }}>Base</TableCell>
                      <TableCell align="right" sx={{ minWidth: 120 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                
                  <TableBody>
                    {conceptos.map((concepto, index) => {
                      const impuesto = impuestos[index];
                    
                      const base = concepto.precio;
                      const iva = base * (impuesto?.cuota ?? 0) / 100;
                      const total = base + iva;
                    
                      return (
                        <TableRow key={index}>
                          <TableCell align="center">
                            <Tooltip title="Eliminar concepto">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  clearGeneralError();
                                  setConceptos(prev => prev.filter((_, i) => i !== index));
                                  setImpuestos(prev => prev.filter((_, i) => i !== index));
                                  setInputPrecios(prev => {
                                    const copy = { ...prev };
                                    delete copy[index];
                                    return copy;
                                  });
                                  setErrors(prev => {
                                    if (!prev.conceptos) return prev;
                                    const nuevos = prev.conceptos.filter((_, i) => i !== index);
                                    return { ...prev, conceptos: nuevos };
                                  });
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                            
                          <TableCell>
                            <TextField
                              variant="outlined"
                              value={concepto.nombre}
                              size="small"
                              onChange={(e) => {
                                const value = e.target.value;
                                clearGeneralError();
                                setConceptos(prev =>
                                  prev.map((c, i) =>
                                    i === index ? { ...c, nombre: value } : c
                                  )
                                );
                                setErrors(prev => {
                                  if (!prev.conceptos?.[index]?.concepto) return prev;

                                  const nuevos = [...(prev.conceptos ?? [])];
                                  nuevos[index] = { ...nuevos[index], concepto: undefined };

                                  return { ...prev, conceptos: nuevos };
                                });
                              }}
                              fullWidth
                              error={!!errors.conceptos?.[index]?.concepto}
                              InputProps={{
                                endAdornment: errors.conceptos?.[index]?.concepto ? (
                                  <Tooltip title={errors.conceptos[index].concepto}>
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                  </Tooltip>
                                ) : undefined,
                              }}
                            />
                          </TableCell>
                            
                          <TableCell align="right">
                            <TextField
                              variant="outlined"
                              type="text"
                              value={inputPrecios[index] ?? concepto.precio.toFixed(2).replace(".", ",")}
                              size="small"
                              error={!!errors.conceptos?.[index]?.precio}
                              InputProps={{
                                endAdornment: errors.conceptos?.[index]?.precio ? (
                                  <Tooltip title={errors.conceptos[index].precio}>
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                  </Tooltip>
                                ) : undefined,
                              }}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (/^\d*([,]\d*)?$/.test(raw)) {
                                  clearGeneralError();
                                  const normalized = raw.replace(",", ".");
                                  const nuevoPrecio = parseFloat(normalized);
                                
                                  setConceptos(prev =>
                                    prev.map((c, i) =>
                                      i === index
                                        ? { ...c, precio: isNaN(nuevoPrecio) ? 0 : nuevoPrecio }
                                        : c
                                    )
                                  );

                                  setErrors(prev => {
                                    if (!prev.conceptos?.[index]?.precio) return prev;

                                    const nuevos = [...(prev.conceptos ?? [])];
                                    nuevos[index] = { ...nuevos[index], precio: undefined };

                                    return { ...prev, conceptos: nuevos };
                                  });
                                
                                  setInputPrecios(prev => ({ ...prev, [index]: raw }));
                                }
                              }}
                              onBlur={(e) => {
                                setInputPrecios(prev => {
                                  const copy = { ...prev };
                                  delete copy[index];
                                  return copy;
                                });
                              }}
                            />
                          </TableCell>
                            
                          <TableCell align="right">
                            <FormControl size="small" sx={{ width: "80px" }}>
                              <Select
                                value={impuesto?.cuota ?? 0}
                                onChange={(e) => {
                                  clearGeneralError();
                                  const value = Number(e.target.value);
                                  setImpuestos(prev =>
                                    prev.map((imp, i) =>
                                      i === index ? { ...imp, cuota: value } : imp
                                    )
                                  );
                                }}
                              >
                                {constantes.IMPUESTOS.map((imp) => (
                                  <MenuItem key={imp} value={imp}>
                                    {imp}%
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                              
                          <TableCell align="right">
                            {formatCurrency(base, monedaFactura)}
                          </TableCell>
                              
                          <TableCell align="right">
                            {formatCurrency(total, monedaFactura)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  
                  </Table>
                </>}

              </AccordionDetails>
            </Accordion>
          </Box>
          )}

          {/* Switch de factura pagada */}
          <FormControlLabel
            control={
              <Switch
                checked={facturaPagada}
                onChange={(e) => {
                  setFacturaPagada(e.target.checked);
                  setMetodoPago(undefined);
                  setErrors((prev: IFacturaComplementariaErrors) => ({
                    ...prev,
                    metodoPago: undefined,
                  }));
                }}
              />
            }
            label="Factura pagada"
            sx={{ m: 0, mb: facturaPagada ? 0 : { xs: 0, md: 7 } }}
          />

          {/* Select de método de pago */}
          {facturaPagada && (
            <Grid container spacing={2} alignItems="flex-start">
              {/* Método de pago */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth error={!!errors.metodoPago}>
                  <InputLabel id="metodo-pago-label">Método de pago*</InputLabel>
                  <Select
                    labelId="metodo-pago-label"
                    value={metodoPago?.nombre || ""}
                    label="Método de pago*"
                    onChange={(e) => {
                      const nombre = e.target.value;
                      const codigo = metodosPago.find((m) => m.nombre === nombre)?.codigo!;
                      setMetodoPago({
                        nombre,
                        codigo,
                        fechaPago: metodoPago?.fechaPago || moment(),
                      });
                      clearGeneralError();
                      setErrors((prev: IFacturaComplementariaErrors) => ({ ...prev, metodoPago: undefined }));
                    }}
                  >
                    {metodosPago.filter((m) => m.activo).map((m) => (
                      <MenuItem key={m.codigo} value={m.nombre}>
                        {m.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.metodoPago && (
                    <Typography color="error" variant="caption">
                      {errors.metodoPago}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
                
              {/* Fecha de pago */}
              <Grid item xs={12} sm={6} md={4}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Fecha de pago"
                    value={metodoPago?.fechaPago}
                    onChange={(date) => {
                      if (metodoPago) {
                        setMetodoPago({ ...metodoPago, fechaPago: date ?? moment() });
                        clearGeneralError();
                        setErrors((prev: IFacturaComplementariaErrors) => ({ ...prev, fechaPago: undefined }));
                      }
                    }}
                    renderInput={(params) => ( <TextField {...params} required fullWidth error={!!errors.fechaPago} helperText={errors.fechaPago} /> )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          )}

          {errors.general && <Alert sx={{ mt: 1 }} severity="warning">{errors.general}</Alert>}

        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={() => {
              if (isRectificativa && !isEditMode) {
                //Crear rectificativa
                validateRectificativa();
              } else if (isRectificativa && isEditMode) { 
                //Editar rectificativa
                handleEditRectificativa();
               } else if (!isRectificativa && isEditMode) {
                //Editar complementaria
                handleEditarFactura();
              } else {
                //Crear complementaria
                handleCrearFactura();
              }
            }} 
            disabled={loading} 
            startIcon={loading && <CircularProgress size={18} />}
          >
            {isRectificativa
              ? isEditMode
                ? "Guardar"
                : "Crear rectificativa"
              : isEditMode
              ? "Guardar"
              : "Crear complementaria"}
          </Button>
        </DialogActions> 
      </>
      }

    </Dialog>

    <Dialog open={confirmOpen} onClose={() => {setConfirmOpen(false); setNuevaFacturaRectificativa(null)}}>
      <DialogTitle>Confirmar creación de rectificativa</DialogTitle>
      <DialogContent>
        <Typography>
          Se generará una factura rectificativa vinculada a la factura original nº: <b>{formatNumeroFactura(facturaComplementariaEdit?.numero)}</b>.
          ¿Deseas continuar?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => {
            setConfirmOpen(false);
            setNuevaFacturaRectificativa(null);
          }}
        >Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleConfirmRectificativa}
          startIcon={loading && <CircularProgress size={18} />}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>

    <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
      <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
        {alert.msg}
      </Alert>
    </Snackbar>
    </>
  );
};

export default ComplementariaFormDialog;